import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  ValidacionesContratantePayload,
  ProgramasContratanteItem
} from '../../models/cotizador-contratante/cotizacion-incompleta.model';
import { GeneralResponse } from '../../models/ciudad/generic-response';
import { CrearCotizacionContratanteService } from './crear-cotizacion-contratante.service';
import { KEY_COTIZACION_CREADA_DESDE_CONTRATANTE } from '../../models/cotizador-contratante/crear cotizacion/cotizacion-creada-contratante-guardada.model';
import { ActualizarCotizacionContratanteValidacionesRequestDTO } from '../../models/cotizador-contratante/update-cotizacion-contratante/request/update-cotizacion-contratante-request.dto';
import { ActualizarCotizacionContratanteValidacionesResponseDTO } from '../../models/cotizador-contratante/update-cotizacion-contratante/response/update-cotizacion-contratante-response.dto';
import { URL_MAP } from '../../shared/urlMap';
import { environment } from '../../../environments/environment';

/** Snapshot de datos del formulario ya sincronizados con BD (solo identidad/contacto/esUsuario). */
export const KEY_DATOS_CONTRATANTE_SYNCED = 'datosRQ002_Contratante_synced';

@Injectable({
  providedIn: 'root'
})
export class CotizacionesIncompletasService {
  private readonly URL_ACTUALIZAR_VALIDACIONES = `${environment.HOST}${URL_MAP.API.COTIZACION_CONTRATANTE_ACTUALIZAR_VALIDACIONES}`;
  private readonly KEY_COTIZACION_PRECARGADA = 'cotizacion_precargada';
  private readonly KEY_DATOS_CONTRATANTE = 'datosRQ002_Contratante';

  constructor(
    private http: HttpClient,
    private crearCotizacionContratanteService: CrearCotizacionContratanteService
  ) {}

  /**
   * Actualiza contratante y reemplaza sus validaciones por completo.
   * Solo llama al backend si los datos del formulario (nombres, contactos, esUsuario, etc.)
   * cambiaron respecto al último snapshot sincronizado. No toca programas.
   */
  actualizarCotizacionIncompletaContratanteValidaciones(
    idCotizacion: number | string,
    payload: ValidacionesContratantePayload
  ): Observable<void> {
    const idCot = Number(idCotizacion);
    if (!Number.isFinite(idCot) || idCot <= 0) return of(undefined);

    const datosStr = localStorage.getItem(this.KEY_DATOS_CONTRATANTE);
    if (!datosStr) return of(undefined);

    let datosContratante: any;
    try {
      datosContratante = JSON.parse(datosStr);
    } catch {
      return of(undefined);
    }

    // Verificar si hay contratos de inclusión nuevos a guardar (solo aplica en flujo inclusión tipoSolicitud=2)
    const tipoSolicitud = localStorage.getItem('tipoSolicitud');
    const esFlujoinclusion = tipoSolicitud === '2';
    const contratosInclusionPendientes = esFlujoinclusion
      ? ((this.crearCotizacionContratanteService as any).mapContratosInclusionFromStorage?.() ?? [])
      : [];
    const hayContratosInclusion = Array.isArray(contratosInclusionPendientes) && contratosInclusionPendientes.length > 0;

    // Solo actualizar BD si el formulario del contratante cambió O si hay contratos de inclusión a persistir.
    if (!this.hayCambiosDatosContratanteFormulario(datosContratante) && !hayContratosInclusion) {
      return of(undefined);
    }

    const idContratanteBD = this.resolverIdContratanteBD();
    if (idContratanteBD == null) {
      console.error(
        '[actualizar-validaciones] idContratante no encontrado en localStorage; se omite el POST para evitar 400.'
      );
      return of(undefined);
    }

    const contratanteBase =
      this.crearCotizacionContratanteService.mapDatosContratanteToDTO(datosContratante as any);

    const contratante = {
      ...contratanteBase,
      idCotizacion: idCot,
      idContratante: idContratanteBD
    };

    const mappedValidaciones =
      this.crearCotizacionContratanteService.mapValidacionesToDTO(payload?.items as any);

    let idsValidaciones: number[] | null = null;
    try {
      const rawPrec = localStorage.getItem(this.KEY_COTIZACION_PRECARGADA);
      if (rawPrec) {
        const prec = JSON.parse(rawPrec);
        if (Array.isArray(prec?.idsValidaciones)) idsValidaciones = prec.idsValidaciones;
      }
    } catch {}

    if (!idsValidaciones) {
      try {
        const rawCreated = localStorage.getItem(KEY_COTIZACION_CREADA_DESDE_CONTRATANTE);
        if (rawCreated) {
          const created = JSON.parse(rawCreated);
          const fromData = created?.data?.idsValidaciones;
          const fromRoot = created?.idsValidaciones;
          if (Array.isArray(fromData)) idsValidaciones = fromData;
          else if (Array.isArray(fromRoot)) idsValidaciones = fromRoot;
        }
      } catch {}
    }

    const validaciones = mappedValidaciones.map((v, idx) => ({
      ...v,
      ...(Array.isArray(idsValidaciones) && Number.isFinite(idsValidaciones[idx])
        ? { idValidacion: idsValidaciones[idx] }
        : {})
    }));

    const contratosInclusion = contratosInclusionPendientes;

    const request: ActualizarCotizacionContratanteValidacionesRequestDTO = {
      contratante,
      validaciones,
      contratosInclusion
    };

    return this.http
      .post<GeneralResponse<ActualizarCotizacionContratanteValidacionesResponseDTO>>(
        this.URL_ACTUALIZAR_VALIDACIONES,
        request
      )
      .pipe(
        tap((resp) => {
          const data = resp?.data;
          if (data) {
            this.persistirIdsTrasUpdate(data);
          }
          this.marcarDatosContratanteSincronizados(datosContratante);
        }),
        map(() => undefined)
      );
  }

  /**
   * true si los campos de identidad/contacto/esUsuario del formulario
   * difieren del último snapshot sincronizado con BD.
   */
  hayCambiosDatosContratanteFormulario(datosActuales?: any): boolean {
    let actual = datosActuales;
    if (!actual) {
      try {
        const raw = localStorage.getItem(this.KEY_DATOS_CONTRATANTE);
        actual = raw ? JSON.parse(raw) : null;
      } catch {
        actual = null;
      }
    }
    if (!actual) return false;

    let synced: any = null;
    try {
      const rawSynced = localStorage.getItem(KEY_DATOS_CONTRATANTE_SYNCED);
      synced = rawSynced ? JSON.parse(rawSynced) : null;
    } catch {
      synced = null;
    }

    // Sin snapshot (p. ej. cotización creada antes de este fix): forzar update una vez.
    if (!synced) return true;

    const a = this.extraerCamposFormularioContratante(actual);
    const b = this.extraerCamposFormularioContratante(synced);
    return JSON.stringify(a) !== JSON.stringify(b);
  }

  /** Guarda snapshot de datos del formulario ya persistidos en BD. */
  marcarDatosContratanteSincronizados(datos?: any): void {
    try {
      let source = datos;
      if (!source) {
        const raw = localStorage.getItem(this.KEY_DATOS_CONTRATANTE);
        source = raw ? JSON.parse(raw) : null;
      }
      if (!source) return;
      localStorage.setItem(
        KEY_DATOS_CONTRATANTE_SYNCED,
        JSON.stringify(this.extraerCamposFormularioContratante(source))
      );
    } catch {
      // no-op
    }
  }

  /** Limpia el snapshot al cambiar de contratante / nueva sesión. */
  limpiarSnapshotDatosContratante(): void {
    try {
      localStorage.removeItem(KEY_DATOS_CONTRATANTE_SYNCED);
    } catch {
      // no-op
    }
  }

  guardarValidaciones(
    idCotizacion: number | string,
    payload: ValidacionesContratantePayload
  ): Observable<void> {
    return this.actualizarCotizacionIncompletaContratanteValidaciones(idCotizacion, payload);
  }

  /**
   * Stub: los programas viven en localStorage (ProgramaSeleccionStore)
   * y se persisten en BD desde Beneficiario. No actualizar CotizacionContratante aquí.
   */
  guardarProgramas(
    idCotizacion: number | string,
    programas: ProgramasContratanteItem[]
  ): Observable<void> {
    return of(undefined);
  }

  /** Lee idContratante BD desde precargada o creada (raíz o data). */
  private resolverIdContratanteBD(): number | null {
    try {
      const rawPrec = localStorage.getItem(this.KEY_COTIZACION_PRECARGADA);
      if (rawPrec) {
        const prec = JSON.parse(rawPrec);
        const v = Number(prec?.idContratante);
        if (Number.isFinite(v) && v > 0) return v;
      }
    } catch {}

    try {
      const rawCreated = localStorage.getItem(KEY_COTIZACION_CREADA_DESDE_CONTRATANTE);
      if (rawCreated) {
        const created = JSON.parse(rawCreated);
        const v = Number(
          created?.data?.idContratante ?? created?.idContratante
        );
        if (Number.isFinite(v) && v > 0) return v;
      }
    } catch {}

    return null;
  }

  private persistirIdsTrasUpdate(
    data: ActualizarCotizacionContratanteValidacionesResponseDTO
  ): void {
    const idContratante = Number(data?.idContratante);
    const idCotizacion = Number(data?.idCotizacion);
    const idsValidaciones = Array.isArray(data?.idsValidaciones) ? data.idsValidaciones : null;

    try {
      const rawCreated = localStorage.getItem(KEY_COTIZACION_CREADA_DESDE_CONTRATANTE);
      if (rawCreated) {
        const created = JSON.parse(rawCreated);
        const next = {
          ...created,
          ...(Number.isFinite(idCotizacion) && idCotizacion > 0 ? { idCotizacion } : {}),
          ...(Number.isFinite(idContratante) && idContratante > 0 ? { idContratante } : {}),
          ...(idsValidaciones ? { idsValidaciones } : {}),
          data: {
            ...(created?.data || {}),
            ...(Number.isFinite(idCotizacion) && idCotizacion > 0 ? { idCotizacion } : {}),
            ...(Number.isFinite(idContratante) && idContratante > 0 ? { idContratante } : {}),
            ...(idsValidaciones ? { idsValidaciones } : {}),
          }
        };
        localStorage.setItem(KEY_COTIZACION_CREADA_DESDE_CONTRATANTE, JSON.stringify(next));
      }
    } catch {}

    try {
      const rawPrec = localStorage.getItem(this.KEY_COTIZACION_PRECARGADA);
      if (rawPrec) {
        const prec = JSON.parse(rawPrec);
        const next = {
          ...prec,
          ...(Number.isFinite(idCotizacion) && idCotizacion > 0 ? { idCotizacion } : {}),
          ...(Number.isFinite(idContratante) && idContratante > 0 ? { idContratante } : {}),
          ...(idsValidaciones ? { idsValidaciones } : {}),
        };
        localStorage.setItem(this.KEY_COTIZACION_PRECARGADA, JSON.stringify(next));
      }
    } catch {}
  }

  private extraerCamposFormularioContratante(datos: any): Record<string, string> {
    const norm = (v: unknown): string => (v ?? '').toString().trim();
    const normFecha = (v: unknown): string => {
      if (v == null || v === '') return '';
      if (v instanceof Date && !isNaN(v.getTime())) {
        const y = v.getFullYear();
        const m = String(v.getMonth() + 1).padStart(2, '0');
        const d = String(v.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
      const s = norm(v);
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
      const d = new Date(s);
      if (!isNaN(d.getTime())) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      }
      return s;
    };
    const esUsuario =
      datos?.esUsuario === true || datos?.esUsuario === 'S' || datos?.esUsuario === 's'
        ? 'S'
        : datos?.esUsuario === false || datos?.esUsuario === 'N' || datos?.esUsuario === 'n'
          ? 'N'
          : norm(datos?.esUsuario);

    return {
      tipoDocumento: norm(datos?.tipoDocumento).toUpperCase(),
      numeroDocumento: norm(datos?.numeroDocumento),
      primerNombre: norm(datos?.primerNombre).toUpperCase(),
      segundoNombre: norm(datos?.segundoNombre).toUpperCase(),
      primerApellido: norm(datos?.primerApellido).toUpperCase(),
      segundoApellido: norm(datos?.segundoApellido).toUpperCase(),
      genero: norm(datos?.genero).toUpperCase(),
      fechaNacimiento: normFecha(datos?.fechaNacimiento),
      emailPrincipal: norm(datos?.emailPrincipal).toLowerCase(),
      emailSecundario: norm(datos?.emailSecundario).toLowerCase(),
      telefono: norm(datos?.telefono),
      telefonoFijo: norm(datos?.telefonoFijo),
      esUsuario,
    };
  }
}
